/**
 * @file file-drop-mixin.ts
 *
 * Conditionally enables drag-and-drop file import on the host element.
 * Activated by the 'file-droppable' attribute; can also be toggled at runtime
 * via attributeChangedCallback in the host class. Dropped files are forwarded
 * as an APP_DROPPED_FILES_EVENT window event for downstream handlers.
 *
 * Exposes: _enableFileDrop, _disableFileDrop
 */
import { DragDropHandler, DropOverlay, APP_DROPPED_FILES_EVENT } from '../../file/fileDrop.ts';
import { type Constructor } from './mixin-types-utils.ts';

/** Surface added by FileDropMixin. */
export interface FileDropMixinMembers {
  /** Activates drag-and-drop file import on the host element. */
  _enableFileDrop(): void;
  /** Deactivates drag-and-drop file import. */
  _disableFileDrop(): void;
}

export const FileDropMixin = <T extends Constructor<HTMLElement>>(Base: T) =>
  class FileDropMixin extends Base {
    private _appDragDropHandler: DragDropHandler | null = null;
    private _appDropOverlay: DropOverlay | null = null;

    async connectedCallback() {
      await super.connectedCallback?.();
      if (this.hasAttribute('file-droppable')) this._enableFileDrop();
    }

    disconnectedCallback() {
      this._disableFileDrop();
      super.disconnectedCallback?.();
    }

    _enableFileDrop(): void {
      if (this._appDragDropHandler) return;
      this._appDropOverlay = new DropOverlay(this.shadowRoot!);
      this._appDragDropHandler = new DragDropHandler(
        this,
        (files) => { window.dispatchEvent(new CustomEvent(APP_DROPPED_FILES_EVENT, { detail: { files } })); },
        this._appDropOverlay,
        false,
      );
    }

    _disableFileDrop(): void {
      this._appDragDropHandler?.destroy();
      this._appDragDropHandler = null;
      this._appDropOverlay?.remove();
      this._appDropOverlay = null;
    }
  };
